import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FaInstagram, FaWhatsapp } from 'react-icons/fa';
import { HiMail, HiPhone, HiLocationMarker, HiArrowRight, HiClock } from 'react-icons/hi';
import { COMPANY } from '../../data/siteData';
import logoImg from '../../assets/logo.png';

const footerLinks = {
  company: [
    { name: 'About Us', path: '/about' },
    { name: 'Our Services', path: '/services' },
    { name: 'Student Stories', path: '/students' },
    { name: 'Blog & Resources', path: '/resources' },
    { name: 'Contact Us', path: '/contact' },
  ],
  destinations: [
    { name: 'Study in UK', path: '/destinations/uk' },
    { name: 'Study in USA', path: '/destinations/usa' },
    { name: 'Study in Canada', path: '/destinations/canada' },
    { name: 'Study in Australia', path: '/destinations/australia' },
    { name: 'Study in Germany', path: '/destinations/germany' },
    { name: 'Study in Switzerland', path: '/destinations/switzerland' },
    { name: 'Study in Ireland', path: '/destinations/ireland' },
  ],
  services: [
    { name: 'Visa Assistance', path: '/services/visa-assistance' },
    { name: 'Loan Assistance', path: '/services/loan-assistance' },
    { name: 'SOP Assistance', path: '/services/sop-assistance' },
    { name: 'Scholarship Guidance', path: '/services/scholarship-assistance' },
    { name: 'University Application', path: '/services' },
    { name: 'Pre-Departure Briefing', path: '/services' },
  ],
  resources: [
    { name: 'Visa Guides', path: '/visa' },
    { name: 'Scholarship Finder', path: '/scholarships' },
    { name: 'University Rankings', path: '/universities' },
    { name: 'Apply Now', path: '/apply' },
    { name: 'Free Consultation', path: '/apply' },
  ],
};

const socialLinks = [
  { icon: FaInstagram, href: COMPANY.social.instagram, label: 'Instagram', color: 'hover:bg-pink-600' },
  { icon: FaWhatsapp, href: `https://wa.me/${COMPANY.whatsapp}`, label: 'WhatsApp', color: 'hover:bg-green-600' },
];

const Footer = () => {
  const [email, setEmail] = useState('');
  const [subscribed, setSubscribed] = useState(false);

  const handleSubscribe = (e) => {
    e.preventDefault();
    if (email) {
      setSubscribed(true);
      setEmail('');
      setTimeout(() => setSubscribed(false), 3000);
    }
  };

  return (
    <footer className="relative bg-primary text-white overflow-hidden">
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-secondary/50 to-transparent" />
      <div className="absolute top-0 right-0 w-96 h-96 bg-secondary/5 rounded-full blur-3xl -translate-y-1/2" />
      <div className="absolute bottom-0 left-0 w-96 h-96 bg-accent/5 rounded-full blur-3xl translate-y-1/2" />

      {/* Newsletter */}
      <div className="relative border-b border-white/10">
        <div className="container-custom py-10 md:py-14">
          <div className="flex flex-col md:flex-row items-center justify-between gap-8">
            <div className="text-center md:text-left max-w-md">
              <h3 className="text-xl md:text-2xl font-bold mb-2">Stay Ahead of the Curve</h3>
              <p className="text-white/60 text-sm md:text-base">
                Get scholarship alerts, visa updates, and expert tips delivered weekly.
              </p>
            </div>
            <form onSubmit={handleSubscribe} className="flex w-full max-w-md">
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Enter your email"
                className="flex-1 px-5 py-3.5 rounded-l-xl bg-white/10 border border-white/15 text-white placeholder:text-white/40 focus:outline-none focus:border-secondary/50 transition-all text-sm"
                required
              />
              <button
                type="submit"
                className="px-6 py-3.5 rounded-r-xl font-semibold text-sm text-white transition-all hover:opacity-90 flex items-center gap-2 whitespace-nowrap"
                style={{ background: 'var(--gradient-cta)' }}
              >
                {subscribed ? '✓ Done!' : 'Subscribe'}
                {!subscribed && <HiArrowRight className="w-4 h-4" />}
              </button>
            </form>
          </div>
        </div>
      </div>

      {/* Main content */}
      <div className="relative container-custom py-12 md:py-16">
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-8 lg:gap-6">
          {/* Brand column */}
          <div className="col-span-2 md:col-span-3 lg:col-span-2 pr-0 lg:pr-8">
            <Link to="/" className="flex items-center gap-3 mb-6 group">
              <div className="h-16 w-auto flex items-center justify-start transition-transform group-hover:scale-105 bg-white rounded-xl p-2.5">
                <img src={logoImg} alt="MasterTech Logo" className="h-full w-auto object-contain" />
              </div>
              <div>
                <span className="text-xl font-black tracking-tight">MasterTech</span>
                <span className="block text-[10px] font-bold text-secondary -mt-0.5 uppercase tracking-widest">Educational</span>
              </div>
            </Link>
            <p className="text-white/50 text-sm leading-relaxed mb-6 max-w-sm">
              {COMPANY.description}
            </p>

            <div className="space-y-3 mb-6">
              <a href={`tel:${COMPANY.phone}`} className="flex items-center gap-3 text-white/60 hover:text-secondary transition-colors text-sm">
                <HiPhone className="w-4 h-4 flex-shrink-0 text-secondary/70" />
                {COMPANY.phone}
              </a>
              <a href={`mailto:${COMPANY.email}`} className="flex items-center gap-3 text-white/60 hover:text-secondary transition-colors text-sm">
                <HiMail className="w-4 h-4 flex-shrink-0 text-secondary/70" />
                {COMPANY.email}
              </a>
              <div className="flex items-start gap-3 text-white/60 text-sm">
                <HiLocationMarker className="w-4 h-4 flex-shrink-0 mt-0.5 text-secondary/70" />
                {COMPANY.address}
              </div>
              <div className="flex items-center gap-3 text-white/60 text-sm">
                <HiClock className="w-4 h-4 flex-shrink-0 text-secondary/70" />
                {COMPANY.hours}
              </div>
            </div>

            <div className="flex items-center gap-2">
              {socialLinks.map((social) => (
                <a
                  key={social.label}
                  href={social.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={social.label}
                  className={`w-9 h-9 rounded-lg bg-white/10 flex items-center justify-center text-white/60 hover:text-white ${social.color} transition-all duration-300 hover:scale-110`}
                >
                  <social.icon className="w-4 h-4" />
                </a>
              ))}
            </div>
          </div>

          {/* Link columns */}
          {[
            { heading: 'Company', links: footerLinks.company },
            { heading: 'Destinations', links: footerLinks.destinations },
            { heading: 'Services', links: footerLinks.services },
            { heading: 'Resources', links: footerLinks.resources },
          ].map(({ heading, links }) => (
            <div key={heading}>
              <h4 className="text-xs font-bold text-white/80 mb-4 uppercase tracking-widest">{heading}</h4>
              <ul className="space-y-2.5">
                {links.map((link) => (
                  <li key={link.name}>
                    <Link to={link.path} className="text-sm text-white/45 hover:text-secondary transition-colors duration-200">
                      {link.name}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </div>

      {/* Bottom bar */}
      <div className="relative border-t border-white/10">
        <div className="container-custom py-6 flex flex-col md:flex-row items-center justify-between gap-4">
          <p className="text-xs text-white/40">
            © {new Date().getFullYear()} MasterTech Educational. All rights reserved. | Founded {COMPANY.founded}
          </p>
          <div className="flex items-center gap-6 text-xs text-white/40">
            <Link to="/about" className="hover:text-white/70 transition-colors">Terms of Service</Link>
            <Link to="/about" className="hover:text-white/70 transition-colors">Privacy Policy</Link>
          </div>
        </div>
      </div>

      {/* Free consultation CTA strip */}
      <motion.div
        initial={{ y: 100, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ delay: 1.5, duration: 0.6 }}
        className="fixed bottom-0 left-0 right-0 z-40 md:hidden bg-primary/95 backdrop-blur-xl border-t border-white/10 px-4 py-3"
      >
        <Link
          to="/apply"
          className="flex items-center justify-center gap-2 w-full py-3 rounded-xl font-bold text-white text-sm"
          style={{ background: 'var(--gradient-cta)' }}
        >
          Book Free Consultation →
        </Link>
      </motion.div>
    </footer>
  );
};

export default Footer;
